/**
 * @fileoverview Push-notification registration hook.
 * @description The one hook a consuming app calls to wire up push notifications end to end: once
 * the user is authenticated, requests notification permission, retrieves an FCM device token, and
 * registers it with the backend; also logs any message received while the tab is open (background
 * delivery is handled separately by public/firebase-messaging-sw.js). A no-op whenever push
 * notifications aren't supported or the user isn't signed in.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useCallback, useEffect } from 'react'

import { useRegisterDeviceToken } from '@/features/notifications/hooks/useRegisterDeviceToken'
import { useAuth } from '@/shared/lib/auth/useAuth'
import { getFcmToken } from '@/shared/lib/notifications/fcm-token.service'
import {
  isPushNotificationSupported,
  requestNotificationPermission,
} from '@/shared/lib/notifications/notification-permission.utils'
import { useForegroundMessages } from '@/shared/lib/notifications/useForegroundMessages'
import { logger } from '@/shared/utils/logger'

/**
 * @description Requests notification permission, retrieves an FCM token, and registers it with
 * the backend while the user is signed in; subscribes to foreground messages for as long as this
 * hook is mounted.
 */
export function useFcmRegistration(): void {
  const { isAuthenticated } = useAuth()
  const { mutate: registerToken } = useRegisterDeviceToken()

  useEffect(() => {
    if (!isAuthenticated || !isPushNotificationSupported()) {
      return
    }

    let cancelled = false

    void (async () => {
      const permission = await requestNotificationPermission()

      if (permission !== 'granted' || cancelled) {
        return
      }

      const token = await getFcmToken()

      if (token && !cancelled) {
        registerToken({ token, platform: 'web' })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, registerToken])

  useForegroundMessages(
    useCallback((payload) => {
      logger.info('Foreground push notification received', { payload })
    }, []),
  )
}
